import { AsyncPipe, NgClass, NgFor, NgIf, NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatToolbar } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { TranslateModule } from '@ngx-translate/core';
import { ContextService } from 'app/core/services/context.service';
import { LocalizationService } from 'app/core/services/localization.service';
import { Language, Languages } from 'app/shared/constants/languages.const';
import { HideOnRouteDirective } from 'app/shared/directives';
import { ClientsService } from 'app/shared/services';
import { NavigationItem, Site, UserInfo } from 'app/shared/types';
import { ThemeService } from 'core/services/theme.service';
import { environment } from 'environment/environment';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { InactivityService } from 'shared/services/inactivity.service';
import { AvatarComponent } from '../../atoms';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  imports: [
    ReactiveFormsModule,
    MatToolbar,
    NgClass,
    MatIcon,
    MatMenuModule,
    TranslateModule,
    MatRadioModule,
    NgFor,
    AsyncPipe,
    UpperCasePipe,
    AvatarComponent,
    MatDivider,
    NgIf,
    NgTemplateOutlet,
    HideOnRouteDirective,
    MatButtonModule,
  ],
})
export class HeaderComponent implements OnInit, OnChanges {
  private authService = inject(AuthService);
  protected clientsService = inject(ClientsService);
  protected contextService = inject(ContextService);
  protected localizationService = inject(LocalizationService);
  protected cdr = inject(ChangeDetectorRef);
  protected themeService = inject(ThemeService);
  protected router = inject(Router);
  protected inactivityService = inject(InactivityService);

  @Input() public navigationItem!: NavigationItem;
  @Input() public menuItems!: string[];
  @Input() public menuState!: string;
  @Input() public sites: Site[] = [];
  @Input() public isSuperAdmin!: boolean | null;

  @Output() public toggleSidebar = new EventEmitter<void>();
  @Output() public headerOptionsToggle = new EventEmitter<boolean>();

  public showHeaderOption = false;
  public selectedMenuItem!: string;
  public languages = Languages;
  public user: UserInfo = {} as UserInfo;
  public form!: FormGroup;
  public isDarkMode: Observable<boolean> | undefined;
  public filteredItems: string[] = [];
  public searchControl = new FormControl('');

  protected searchControlSubscription = this.searchControl.valueChanges.subscribe((value) => this.filterItems(value));

  constructor() {
    this.initializeForm();
  }

  public ngOnInit(): void {
    this.inactivityService.startInactivityTracking();
    this.isDarkMode = this.themeService.darkMode$;
    this.authService.user$?.subscribe((user) => {
      if (!user) return;
      this.user.firstName = user.given_name ?? '';
      this.user.lastName = user.family_name ?? '';
      this.user.email = user.email ?? '';
      this.user.fullName = `${this.user.firstName} ${this.user.lastName}`;
      if (!this.sites.length) {
        this.menuItems = [];
        this.setFilteredList();
        this.selectedMenuItem = '';
      }
    });
  }

  public initializeForm(): void {
    this.form = new FormGroup({
      language: new FormControl(this.languages[0]),
      menuItem: new FormControl(this.selectedMenuItem),
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.menuState === 'out' && this.showHeaderOption) {
      this.showHeaderOption = false;
    }
    if ('sites' in changes) {
      const newSites = changes['sites'].currentValue;
      const previousSites = changes['sites'].previousValue;
      if (newSites && newSites.length > 0) {
        if (!_.isEqual(newSites, previousSites)) {
          this.setSites(newSites);
        }
      } else if (newSites?.length === 0) {
        this.menuItems = [];
        this.setFilteredList();
        this.selectedMenuItem = '';
        this.contextService.site.next(null);
      }
    }

    this.setFilteredList();
  }

  public filterItems(query: string | null): void {
    if (!this.menuItems) return;
    const search = query?.toLowerCase() || '';
    this.filteredItems = this.menuItems
      .filter((item) => item.toLowerCase().includes(search) || (item && item.toLowerCase().includes(search)))
      .sort();
  }

  public onMenuClosed(): void {
    if (!this.menuItems) return;
    this.searchControl.setValue('');
    this.setFilteredList();
  }

  public changeLanguage(language: string): void {
    this.localizationService.setLanguage(language);
  }

  public getAvailableLanguages(languages: Language[]): Language[] {
    return languages.filter((language) => language.available);
  }

  public getLaunchingSoonLanguages(languages: Language[]): Language[] {
    return languages.filter((language) => !language.available);
  }

  public triggerToggleMenu(): void {
    if (this.showHeaderOption) {
      this.showHeaderOption = !this.showHeaderOption;
    }
    this.toggleSidebar.emit();
  }

  public toggleHeaderMenu(): void {
    this.showHeaderOption = !this.showHeaderOption;
    this.headerOptionsToggle.emit(this.showHeaderOption);
    if (this.showHeaderOption && this.menuState === 'out') {
      this.toggleSidebar.emit();
    }
  }

  public logout(): void {
    sessionStorage.removeItem('refreshFlagger');
    this.contextService.userInfo.next(null);
    this.inactivityService.stopInactivityTracking();
    this.authService.logout({
      logoutParams: {
        returnTo: environment.auth0RedirectUrl,
      },
    });
  }

  public changeSite(item: string): void {
    const selectedSite = this.sites.find((site) => site.name === item);
    if (selectedSite) {
      this.contextService.updateSiteContext(selectedSite);

      this.router.navigate(['/home']);
      this.selectedMenuItem = item;
    }
  }

  public toggleThemeMode(): void {
    this.themeService.toggleMode();
  }

  private setSites(sites: Site[]): void {
    if (!sites.length) return;
    this.menuItems = sites.map((site) => site.name);
    let preselectedItem = this.menuItems[0];

    if (sites.length) {
      preselectedItem =
        sites.find((site) => site.id === this.contextService.siteId.getValue())?.name || this.menuItems[0];
    }

    this.selectedMenuItem = preselectedItem;
    const selectedSite = this.sites.find((site) => site.name === this.selectedMenuItem);

    this.contextService.updateSiteContext(selectedSite!);
    // We need to prevent redirecting to home on refresh if user is not super admin
    // Refresh triggers setSites because it is called in ngOnChanges
    const hasRefreshed = sessionStorage.getItem('refreshFlagger');

    if (!hasRefreshed) {
      this.changeSite(preselectedItem);
      sessionStorage.setItem('refreshFlagger', 'true');
    }

    this.form.get('menuItem')?.setValue(this.selectedMenuItem);
    this.cdr.detectChanges();
  }

  public trackByIndex(index: number): number {
    return index;
  }

  private setFilteredList(): void {
    this.filteredItems = [...this.menuItems.sort()];
  }
}
